import { useEffect, useState } from 'react'
import { api, ApiError } from '@/lib/api'
import type { GameState, Results } from '@/lib/api'
import { formatDuration } from '@/lib/format'

type Props = { game: GameState; onLogout: () => void }

/** Final results, score first. Time is secondary; penalties stay server/admin-side. */
export function FinishedScreen({ game, onLogout }: Props) {
  const [results, setResults] = useState<Results | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .results()
      .then((r) => setResults(r.results))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load results'))
  }, [])

  return (
    <main>
      <h1>{game.event.name}</h1>
      <section className="card">
        <h2>You finished!</h2>
        {error && (
          <p className="status-err" role="alert">
            {error}
          </p>
        )}
        {!results && !error && <p className="muted">Calculating your results…</p>}
        {results && (
          <>
            <p className="final-time">
              <span className="muted">Your score</span>
              <strong>
                {results.score} <span className="muted">/ {results.maxScore}</span>
              </strong>
            </p>
            <dl>
              <dt>Puzzles solved</dt>
              <dd>
                {results.puzzlesCompleted} / {results.totalPuzzles}
              </dd>
              <dt>Time</dt>
              <dd>{formatDuration(results.rawSeconds ?? 0)}</dd>
              <dt>Wrong answers</dt>
              <dd>{results.wrongAttempts}</dd>
              <dt>Hints used</dt>
              <dd>{results.hintsUsed}</dd>
            </dl>
            <details className="breakdown">
              <summary>Per-puzzle breakdown</summary>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Puzzle</th>
                      <th>Result</th>
                      <th>Points</th>
                      <th>Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.puzzles.map((p, i) => (
                      <tr key={p.id} className={p.status === 'failed' ? 'status-err' : undefined}>
                        <td>{i + 1}</td>
                        <td>{p.title}</td>
                        <td>{p.status === 'completed' ? 'Solved' : p.status === 'failed' ? 'Not solved' : '—'}</td>
                        <td>
                          {p.pointsAwarded} / {p.pointsPossible}
                        </td>
                        <td>{p.seconds === null ? '—' : formatDuration(p.seconds)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          </>
        )}
        <p>
          <button type="button" onClick={onLogout}>
            Log out
          </button>
        </p>
      </section>
    </main>
  )
}
